import { useState, useEffect, useCallback } from 'react';
import PropTypes from 'prop-types';
import WeaponeeringAPI from '../services/WeaponeeringAPIClient';

/**
 * Custom hook for weaponeering entity search with debouncing and filters
 *
 * Provides fuzzy search across sites, facilities, and DMPIs by:
 * - Short-code (SIT-001, FAC-001, DMP-001)
 * - Name (fuzzy matching)
 * - Business ID
 *
 * Features:
 * - 300ms debouncing to prevent excessive API calls
 * - Entity type filtering (site, facility, dmpi)
 * - Lifecycle stage filtering
 * - Theater/COCOM filtering
 * - Returns full lineage (Site → Facility → DMPI)
 *
 * @returns {Object} Search state and actions
 */

/**
 * Build the POST body for /api/weaponeering/search from the hook's query
 * text and filter state.
 *
 * WeaponeeringSearchRequest (backend/app/schemas/weaponeering.py) names its
 * fields `entity_types` (a list), `theater_id`, and `analyst_assigned` --
 * the old body sent `entity_type`, `theater`, and `analyst`, which don't
 * match any field on that Pydantic model. FastAPI silently drops
 * unrecognized JSON keys rather than erroring, so every filter chip was a
 * complete no-op. Exported standalone so the field-name mapping is
 * unit-testable without mounting the hook.
 *
 * @param {string} query
 * @param {Object} filters - hook's filters state (entity_type, lifecycle_stage, theater, analyst, ai_suggested)
 */
export function buildWeaponeeringSearchRequestBody(query, filters) {
  return {
    query: query.trim(),
    entity_types: filters.entity_type ? [filters.entity_type] : null,
    lifecycle_stage: filters.lifecycle_stage,
    theater_id: filters.theater,
    analyst_assigned: filters.analyst,
    ai_suggested: filters.ai_suggested,
    limit: 50,
    offset: 0,
  };
}

export function useWeaponeeringSearch() {
  const [query, setQuery] = useState('');
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [total, setTotal] = useState(0);
  const [filters, setFilters] = useState({
    entity_type: null, // 'site' | 'facility' | 'dmpi' | null (all)
    lifecycle_stage: null,
    theater: null,
    analyst: null,
    ai_suggested: null,
  });

  const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || 'http://localhost:8000';

  /**
   * Execute search with current query and filters
   */
  const executeSearch = useCallback(async () => {
    if (!query.trim()) {
      setResults([]);
      setTotal(0);
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const token = await WeaponeeringAPI.resolveToken();
      const response = await fetch(`${API_BASE_URL}/api/weaponeering/search`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        body: JSON.stringify(buildWeaponeeringSearchRequestBody(query, filters)),
      });

      if (!response.ok) {
        throw new Error(`Search failed: ${response.statusText}`);
      }

      const data = await response.json();
      setResults(data.results || []);
      setTotal(data.total || 0);
    } catch (err) {
      console.error('Search error:', err);
      setError(err.message);
      setResults([]);
      setTotal(0);
    } finally {
      setLoading(false);
    }
  }, [query, filters, API_BASE_URL]);

  /**
   * Debounced search effect
   * Waits 300ms after last keystroke before executing search
   */
  useEffect(() => {
    if (!query.trim()) {
      setResults([]);
      setTotal(0);
      setLoading(false);
      return;
    }

    const debounceTimer = setTimeout(() => {
      executeSearch();
    }, 300); // 300ms debounce

    return () => clearTimeout(debounceTimer);
  }, [query, filters, executeSearch]);

  /**
   * Update filters
   */
  const updateFilters = useCallback((newFilters) => {
    setFilters((prev) => ({
      ...prev,
      ...newFilters,
    }));
  }, []);

  /**
   * Clear all filters
   */
  const clearFilters = useCallback(() => {
    setFilters({
      entity_type: null,
      lifecycle_stage: null,
      theater: null,
      analyst: null,
      ai_suggested: null,
    });
  }, []);

  /**
   * Clear search and results
   */
  const clearSearch = useCallback(() => {
    setQuery('');
    setResults([]);
    setTotal(0);
    setError(null);
  }, []);

  /**
   * Manually trigger search (useful for filter changes without query change)
   */
  const refresh = useCallback(() => {
    executeSearch();
  }, [executeSearch]);

  return {
    // Search state
    query,
    setQuery,
    results,
    loading,
    error,
    total,

    // Filters
    filters,
    updateFilters,
    clearFilters,

    // Actions
    clearSearch,
    refresh,
  };
}

/**
 * PropTypes for search result objects
 */
export const WeaponeeringSearchResultPropType = PropTypes.shape({
  id: PropTypes.string.isRequired,
  entity_type: PropTypes.oneOf(['site', 'facility', 'dmpi']).isRequired,
  short_code: PropTypes.string,
  name: PropTypes.string.isRequired,
  business_id: PropTypes.string,
  lifecycle_stage: PropTypes.string,
  theater: PropTypes.string,
  coordinates: PropTypes.shape({
    lat: PropTypes.number,
    lon: PropTypes.number,
  }),
  ai_suggested: PropTypes.bool,
  // Lineage (full hierarchy)
  site_name: PropTypes.string,
  facility_name: PropTypes.string,
  dmpi_name: PropTypes.string,
});

export default useWeaponeeringSearch;
